import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Alert, Box, Divider, IconButton, Slide, Snackbar, Typography, useMediaQuery, useTheme } from '@mui/material'
import ClockInDrawer from './ClockInDrawer'
import SnackbarRecruitment from 'module/recruitment/reusable/SnackbarRecruitment'

import { parseISO, format, differenceInSeconds } from 'date-fns'
import { formatTimer, formatTimeToAmPm } from 'services/helper/DateTime'

// icons
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined'
import CloseIcon from '@mui/icons-material/Close'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'

const normalizeOutOfRadiusAction = (value) => {
  if (value === 'warn' || value === 2 || value === '2') return 'warn'
  return 'block'
}

const getShiftZoneConfig = (shift) => {
  const locationConfig = shift?.location_config
  const enabled = Boolean(locationConfig?.enabled ?? shift?.use_default_location)

  if (!enabled) return null

  return {
    enabled,
    latitude:
      locationConfig?.location_latitude ?? shift?.location_latitude ?? shift?.latitude ?? null,
    longitude:
      locationConfig?.location_longitude ?? shift?.location_longitude ?? shift?.longitude ?? null,
    radius:
      locationConfig?.allowed_radius_meters ?? shift?.allowed_radius_meters ?? shift?.radius ?? null,
    out_of_radius_action: normalizeOutOfRadiusAction(
      locationConfig?.out_of_radius_action ?? shift?.out_of_radius_action
    ),
    zone_name: locationConfig?.location_name ?? shift?.location_name ?? shift?.zone_name ?? null,
    zone_address:
      locationConfig?.location_address ?? shift?.location_address ?? shift?.zone_address ?? null,
  }
}

const WarningSlideTransition = (props) => <Slide {...props} direction="down" />

const TimeAttendancePanel = ({ data }) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  const shift = data?.shift
  const hasShiftToday = !!shift && (shift.status || '').toLowerCase() === 'active'
  const effectiveShift = hasShiftToday ? shift : null

  // IMPORTANT: Your API shape is (data.attendanceToday.attendance)
  const attendanceToday = data?.attendanceToday
  const attendance = attendanceToday?.attendance ?? null

  const hasClockIn = !!attendance?.clockin?.clockin
  const hasClockOut = !!attendance?.clockout?.clockout
  const attendanceId = attendance?.id ?? null

  // Use created_at to compute accurate timer baseline
  const clockInCreatedAt = attendance?.clockin?.created_at ?? null

  // Pause-related fields (Dashboard is "right" because it relies on attendanceToday-level flags)
  const pausedAt = attendanceToday?.paused_at ?? attendance?.paused_at ?? null
  const totalPausedSeconds = Number(
    attendanceToday?.total_paused_seconds ??
    attendance?.total_paused_seconds ??
    0
  )

  const apiPaused =
    attendanceToday?.is_paused ??
    attendanceToday?.paused ??
    attendance?.is_paused ??
    attendance?.paused ??
    false

  const [successSnackbarOpen, setSuccessSnackbarOpen] = useState('')
  const [errorSnackbarOpen, setErrorSnackbarOpen] = useState('')
  const [warningSnackbarOpen, setWarningSnackbarOpen] = useState('')

  const [isPaused, setIsPaused] = useState(false)
  const [pausedTimeSnapshot, setPausedTimeSnapshot] = useState(null)
  const [time, setTime] = useState(0)

  const baselineMsRef = useRef(null)
  const baselineSecondsRef = useRef(0)
  const intervalRef = useRef(null)

  const formattedDateToday = useMemo(() => {
    if (!attendanceToday?.date) return ''
    try {
      const date = parseISO(attendanceToday.date)
      return format(date, 'MMM d, yyyy')
    } catch (e) {
      console.error('Invalid date string:', attendanceToday.date, e)
      return ''
    }
  }, [attendanceToday?.date])

  const [isCustomHours, setIsCustomHours] = useState(false)

  useEffect(() => {
    if (!shift?.working_time?.length) return

    const allSame = shift.working_time.every((t, idx) => {
      const first = shift.working_time[0]
      if (first.total_hours) return t.total_hours === first.total_hours
      return t.start_time === first.start_time && t.end_time === first.end_time
    })

    setIsCustomHours(!allSame)
  }, [shift])

  // ===== P0: Correct timer bootstrap (prevents reset to 0 + matches dashboard pause behaviour) =====
  useEffect(() => {
    setIsPaused(!!apiPaused)

    // Reset cases
    if (!hasClockIn || hasClockOut || !clockInCreatedAt) {
      setTime(0)
      setPausedTimeSnapshot(null)
      baselineMsRef.current = null
      baselineSecondsRef.current = 0
      if (intervalRef.current) clearInterval(intervalRef.current)
      intervalRef.current = null
      return
    }

    const clockedInTime = new Date(clockInCreatedAt)
    if (isNaN(clockedInTime.getTime())) {
      setTime(0)
      setPausedTimeSnapshot(null)
      if (intervalRef.current) clearInterval(intervalRef.current)
      intervalRef.current = null
      return
    }

    // If paused, freeze at pausedAt (so it does NOT keep incrementing)
    const referenceTime =
      apiPaused && pausedAt ? new Date(pausedAt) : new Date()

    let seconds = Math.max(0, differenceInSeconds(referenceTime, clockedInTime))

    // Subtract accumulated paused seconds across previous pauses
    seconds = Math.max(0, seconds - totalPausedSeconds)

    setTime(seconds)
    setPausedTimeSnapshot(apiPaused ? seconds : null)

    baselineMsRef.current = Date.now()
    baselineSecondsRef.current = seconds

    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
  }, [
    attendanceId,
    hasClockIn,
    hasClockOut,
    clockInCreatedAt,
    apiPaused,
    pausedAt,
    totalPausedSeconds,
  ])

  // ===== Run/stop interval =====
  useEffect(() => {
    if (!hasClockIn || hasClockOut || !clockInCreatedAt) {
      if (intervalRef.current) clearInterval(intervalRef.current)
      intervalRef.current = null
      return
    }

    // paused -> freeze
    if (isPaused) {
      if (pausedTimeSnapshot == null) setPausedTimeSnapshot(time)
      if (intervalRef.current) clearInterval(intervalRef.current)
      intervalRef.current = null
      return
    }

    // resumed -> run
    if (pausedTimeSnapshot != null) setPausedTimeSnapshot(null)

    baselineMsRef.current = Date.now()
    baselineSecondsRef.current = time

    if (intervalRef.current) clearInterval(intervalRef.current)
    intervalRef.current = setInterval(() => {
      const now = Date.now()
      const baseMs = baselineMsRef.current ?? now
      const baseSec = baselineSecondsRef.current ?? 0
      const delta = Math.floor((now - baseMs) / 1000)
      setTime(baseSec + delta)
    }, 1000)

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
      intervalRef.current = null
    }
  }, [hasClockIn, hasClockOut, clockInCreatedAt, isPaused, time, pausedTimeSnapshot])

  const displayTime = isPaused ? (pausedTimeSnapshot ?? time) : time
  const shiftZone = useMemo(() => getShiftZoneConfig(effectiveShift), [effectiveShift])

  return (
    <>
      {isMobile ? (
        <MobileView
          shift={effectiveShift}
          data={attendance}
          date={formattedDateToday || ''}
          setSuccessSnackbarOpen={setSuccessSnackbarOpen}
          setErrorSnackbarOpen={setErrorSnackbarOpen}
          setWarningSnackbarOpen={setWarningSnackbarOpen}
          time={displayTime}
          isCustomHours={isCustomHours}
          isPaused={isPaused}
          setIsPaused={setIsPaused}
          pausedTimeSnapshot={pausedTimeSnapshot}
          setPausedTimeSnapshot={setPausedTimeSnapshot}
          shiftZone={shiftZone}
        />
      ) : (
        <DesktopView
          shift={effectiveShift}
          data={attendance}
          date={formattedDateToday || ''}
          setSuccessSnackbarOpen={setSuccessSnackbarOpen}
          setErrorSnackbarOpen={setErrorSnackbarOpen}
          setWarningSnackbarOpen={setWarningSnackbarOpen}
          time={displayTime}
          isCustomHours={isCustomHours}
          isPaused={isPaused}
          setIsPaused={setIsPaused}
          pausedTimeSnapshot={pausedTimeSnapshot}
          setPausedTimeSnapshot={setPausedTimeSnapshot}
          shiftZone={shiftZone}
        />
      )}

      {!!successSnackbarOpen && (
        <SnackbarRecruitment
          autoHideDuration={3000}
          status="success"
          TransitionComponent="slide"
          message={successSnackbarOpen}
          open={!!successSnackbarOpen}
          handleClose={() => setSuccessSnackbarOpen('')}
        />
      )}

      {!!errorSnackbarOpen && (
        <SnackbarRecruitment
          autoHideDuration={3000}
          status="error"
          TransitionComponent="slide"
          message={errorSnackbarOpen}
          open={!!errorSnackbarOpen}
          handleClose={() => setErrorSnackbarOpen('')}
        />
      )}

      {!!warningSnackbarOpen && (
        <Snackbar
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          open={!!warningSnackbarOpen}
          onClose={() => setWarningSnackbarOpen('')}
          autoHideDuration={4000}
          TransitionComponent={WarningSlideTransition}
        >
          <Alert
            severity="warning"
            variant="filled"
            icon={<WarningAmberIcon sx={{ fontSize: 20, color: '#000' }} />}
            action={
              <IconButton
                size="small"
                aria-label="close"
                color="inherit"
                onClick={() => setWarningSnackbarOpen('')}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            }
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <Typography color="black">{warningSnackbarOpen}</Typography>
          </Alert>
        </Snackbar>
      )}
    </>
  )
}

export default TimeAttendancePanel

const MobileView = ({
  shift,
  data,
  date,
  setSuccessSnackbarOpen,
  setErrorSnackbarOpen,
  setWarningSnackbarOpen,
  time,
  isCustomHours,
  isPaused,
  setIsPaused,
  pausedTimeSnapshot,
  setPausedTimeSnapshot,
  shiftZone,
}) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '32px',
        padding: '20px',
        backgroundColor: 'white.main',
        border: 1,
        borderRadius: '4.5px',
        borderColor: 'gray-line.main',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '16px',
          paddingX: '16px',
          paddingY: '12px',
          background: 'linear-gradient(to right, #EF6617, #F3BF3A, #F27C19)',
          color: 'white.main',
          borderRadius: '4.5px',
        }}
      >
        <Typography>{shift ? "You've got a shift today" : 'No shift to clock in today'}</Typography>
        <Typography sx={{ width: 'full' }}>{date}</Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '20px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'default-background.main',
            borderRadius: '100%',
            border: 1,
            borderColor: 'gray-line.main',
            width: '85px',
            height: '85px',
          }}
        >
          <TimerOutlinedIcon
            sx={{ color: shift ? 'alternate.main' : 'gray-line.main', fontSize: '32px' }}
          />
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '4px' }}>
          <Typography
            sx={{
              fontSize: '18px',
              color: shift ? 'dark-text.main' : 'light-gray-text-2.main',
            }}
          >
            {`${formatTimer(time)} hrs`}
          </Typography>
          <Typography sx={{ color: shift ? 'light-gray-text.main' : 'light-gray-text-2.main' }}>
            {shift ? (isPaused ? 'Paused' : time !== 0 ? 'Clocked in' : 'Not started yet') : 'No shift today'}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', paddingX: '32px', width: '100%' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '4px' }}>
            <Typography sx={{ fontSize: '18px', color: shift ? 'dark-text.main' : 'light-gray-text-2.main' }}>
              {data?.clockin?.clockin || '--:--'}
            </Typography>
            <Typography sx={{ color: shift ? 'dark-text.main' : 'light-gray-text-2.main' }}>
              Clock In
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '4px' }}>
            <Typography sx={{ fontSize: '18px', color: shift ? 'dark-text.main' : 'light-gray-text-2.main' }}>
              {data?.clockout?.clockout || '--:--'}
            </Typography>
            <Typography sx={{ color: shift ? 'dark-text.main' : 'light-gray-text-2.main' }}>
              Clock Out
            </Typography>
          </Box>
        </Box>

        <Divider />

        {shift ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px', width: '100%' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '4px' }}>
              <Typography sx={{ fontSize: '18px' }}>
                {isCustomHours
                  ? 'Custom hours'
                  : shift.working_time?.[0]?.total_hours
                    ? `${shift.working_time[0].total_hours} hours`
                    : shift.working_time?.[0]?.start_time && shift.working_time?.[0]?.end_time
                      ? `${formatTimeToAmPm(shift.working_time[0].start_time, true)} - ${formatTimeToAmPm(shift.working_time[0].end_time, true)}`
                      : 'No shift'}
              </Typography>
              <Typography sx={{ color: 'light-gray-text.main' }}>{shift.name}</Typography>
            </Box>

            <ClockInDrawer
              data={data}
              shiftStatus={shift?.status}
              shiftZone={shiftZone}
              hasShift={true}
              setSuccessSnackbarOpen={setSuccessSnackbarOpen}
              setErrorSnackbarOpen={setErrorSnackbarOpen}
              setWarningSnackbarOpen={setWarningSnackbarOpen}
              time={time}
              isPaused={isPaused}
              setIsPaused={setIsPaused}
              showPauseResumeOnButton
              pausedTimeSnapshot={pausedTimeSnapshot}
              setPausedTimeSnapshot={setPausedTimeSnapshot}
            />
          </Box>
        ) : (
            <ClockInDrawer
              data={data}
              shiftStatus={null}
              hasShift={false}
              setSuccessSnackbarOpen={setSuccessSnackbarOpen}
              setErrorSnackbarOpen={setErrorSnackbarOpen}
              setWarningSnackbarOpen={setWarningSnackbarOpen}
              time={time}
            isPaused={isPaused}
            setIsPaused={setIsPaused}
          />
        )}
      </Box>
    </Box>
  )
}

const DesktopView = ({
  shift,
  data,
  date,
  setSuccessSnackbarOpen,
  setErrorSnackbarOpen,
  setWarningSnackbarOpen,
  time,
  isCustomHours,
  isPaused,
  setIsPaused,
  pausedTimeSnapshot,
  setPausedTimeSnapshot,
  shiftZone,
}) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        padding: '20px',
        backgroundColor: 'white.main',
        border: 1,
        borderRadius: '4.5px',
        borderColor: 'gray-line.main',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '16px',
          paddingX: '16px',
          paddingY: '12px',
          background: 'linear-gradient(to right, #EF6617, #F3BF3A, #F27C19)',
          color: 'white.main',
          borderRadius: '4.5px',
        }}
      >
        <Typography>{shift ? "You've got a shift today" : 'No shift to clock in today'}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <CalendarTodayIcon sx={{ fontSize: '18px' }} />
          <Typography sx={{ width: 'full' }}>{date}</Typography>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', width: shift ? '60%' : '100%', justifyContent: 'space-between', paddingRight: '32px' }}>
          <Box sx={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: 'default-background.main',
                borderRadius: '100%',
                border: 1,
                borderColor: 'gray-line.main',
                width: '65px',
                height: '65px',
              }}
            >
              <TimerOutlinedIcon sx={{ color: shift ? 'alternate.main' : 'gray-line.main', fontSize: '28px' }} />
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '4px' }}>
              <Typography sx={{ fontSize: '18px', color: shift ? 'dark-text.main' : 'light-gray-text-2.main' }}>
                {`${formatTimer(time)} hrs`}
              </Typography>
              <Typography sx={{ color: shift ? 'light-gray-text.main' : 'light-gray-text-2.main' }}>
                {shift ? (isPaused ? 'Paused' : time !== 0 ? 'Clocked in' : 'Not started yet') : 'No shift today'}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: '16px' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '4px' }}>
              <Typography sx={{ fontSize: '18px', color: shift ? 'dark-text.main' : 'light-gray-text-2.main' }}>
                {data?.clockin?.clockin || '--:--'}
              </Typography>
              <Typography sx={{ color: shift ? 'dark-text.main' : 'light-gray-text-2.main' }}>Clock In</Typography>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '4px' }}>
              <Typography sx={{ fontSize: '18px', color: shift ? 'dark-text.main' : 'light-gray-text-2.main' }}>
                {data?.clockout?.clockout || '--:--'}
              </Typography>
              <Typography sx={{ color: shift ? 'dark-text.main' : 'light-gray-text-2.main' }}>Clock Out</Typography>
            </Box>
          </Box>
        </Box>

        {shift ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '40%',
              borderLeft: 1,
              borderColor: 'gray-line.main',
              paddingLeft: '32px',
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '4px' }}>
              <Typography sx={{ fontSize: '18px' }}>
                {isCustomHours
                  ? 'Custom hours'
                  : shift.working_time?.[0]?.total_hours
                    ? `${shift.working_time[0].total_hours} hours`
                    : shift.working_time?.[0]?.start_time && shift.working_time?.[0]?.end_time
                      ? `${formatTimeToAmPm(shift.working_time[0].start_time, true)} - ${formatTimeToAmPm(shift.working_time[0].end_time, true)}`
                      : 'No shift'}
              </Typography>
              <Typography sx={{ color: 'light-gray-text.main' }}>{shift.name}</Typography>
            </Box>

            <ClockInDrawer
              data={data}
              shiftStatus={shift?.status}
              shiftZone={shiftZone}
              hasShift={true}
              time={time}
              fullWidth
              setSuccessSnackbarOpen={setSuccessSnackbarOpen}
              setErrorSnackbarOpen={setErrorSnackbarOpen}
              setWarningSnackbarOpen={setWarningSnackbarOpen}
              isPaused={isPaused}
              setIsPaused={setIsPaused}
              showPauseResumeOnButton
              pausedTimeSnapshot={pausedTimeSnapshot}
              setPausedTimeSnapshot={setPausedTimeSnapshot}
              buttonSx={{
                width: 170,
                minWidth: 170,
                alignSelf: 'center',
              }}
            />
          </Box>
        ) : (
            <ClockInDrawer
              data={data}
              shiftStatus={null}
              hasShift={false}
              setSuccessSnackbarOpen={setSuccessSnackbarOpen}
              setErrorSnackbarOpen={setErrorSnackbarOpen}
              setWarningSnackbarOpen={setWarningSnackbarOpen}
              time={time}
            isPaused={isPaused}
            setIsPaused={setIsPaused}
            buttonSx={{
              width: 170,
              minWidth: 170,
              alignSelf: 'center',
            }}
          />
        )}
      </Box>
    </Box>
  )
}
